// IM-UX-001 (IM-UI-079) — experiência guiada e divulgação progressiva. Componentes compartilhados pelos módulos
// de estratégia (TAM/SAM/SOM, PESTEL e Cinco Forças, Battlecards, Segmentação):
//  - ModuleIntro: a pergunta comercial que o módulo responde, a metodologia (sempre visível) em linguagem simples e o que
//    o InteliMove faz × o que a equipe revisa; a explicação completa abre sob demanda ("Entenda esta análise").
//  - Disclosure: seção expansível acessível (details/summary nativo: teclado, foco e estado aberto/fechado).
//  - CredibilitySummary: resumo metodológico padronizado (metodologia, fontes, período, atualização, confiança, natureza
//    das afirmações, lacunas e versão). Só mostra o que o módulo informa; dado ausente vira estado honesto, nunca zero.
//  - ActionGuide: o que cada ação faz antes de executar (dados novos? versão nova? a atual é preservada?).
import { useEffect, useId, useState, type ReactNode } from 'react';
import { StatusPill } from './Widgets';

export interface ModuleGuide {
  /** Pergunta comercial respondida pelo módulo. */
  question: string;
  /** Nome da metodologia, sempre visível. */
  method: string;
  /** Explicação simples (uma ou duas frases). */
  summary: string;
  /** Para que serve, em termos comerciais. */
  purpose: string;
  /** O resultado que será produzido. */
  output: string;
  /** O que o InteliMove faz automaticamente. */
  automatic: string;
  /** O que pode precisar de revisão da equipe. */
  review: string;
  /** Explicação completa (aberta por "Entenda esta análise"). */
  details: ReactNode;
}

function GuideFacts({ g }: { g: ModuleGuide }) {
  return (
    <dl className="gd-facts">
      <div><dt>Para que serve</dt><dd>{g.purpose}</dd></div>
      <div><dt>O que você recebe</dt><dd>{g.output}</dd></div>
      <div><dt>O InteliMove faz</dt><dd>{g.automatic}</dd></div>
      <div><dt>Você revisa</dt><dd>{g.review}</dd></div>
    </dl>
  );
}

/** Contextualização no topo do módulo. `compact`: nas telas de detalhe, só pergunta + metodologia + explicação curta. */
export function ModuleIntro({ guide, compact = false }: { guide: ModuleGuide; compact?: boolean }) {
  const id = useId();
  return (
    <section className={`card gd-intro${compact ? ' compact' : ''}`} aria-labelledby={`${id}-q`}>
      <p className="gd-method"><span className="gd-method-k">Metodologia</span> <b>{guide.method}</b></p>
      <h2 className="gd-question" id={`${id}-q`}>{guide.question}</h2>
      <p className="gd-summary">{guide.summary}</p>
      {!compact && <GuideFacts g={guide} />}
      <details className="gd-more">
        <summary>{compact ? 'Entenda esta análise' : 'Entenda esta análise em detalhe'}</summary>
        <div className="gd-more-body">
          {compact && <GuideFacts g={guide} />}
          {guide.details}
        </div>
      </details>
    </section>
  );
}

/** Seção expansível. `open` força a abertura (ex.: um editor aberto dentro dela). */
export function Disclosure({ title, hint, children, defaultOpen = false, open, className = '', headingLevel }: {
  title: ReactNode; hint?: ReactNode; children: ReactNode; defaultOpen?: boolean; open?: boolean; className?: string; headingLevel?: 2 | 3;
}) {
  const [isOpen, setOpen] = useState(defaultOpen || !!open);
  useEffect(() => { if (open) setOpen(true); }, [open]);
  const H = headingLevel === 2 ? 'h2' : headingLevel === 3 ? 'h3' : 'span';
  return (
    <details className={`gd-disclosure ${className}`} open={isOpen} onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}>
      <summary><H className="gd-d-title">{title}</H>{hint ? <span className="gd-d-hint">{hint}</span> : null}</summary>
      <div className="gd-d-body">{children}</div>
    </details>
  );
}

export interface NatureCount { label: string; count: number | null; tone?: string; hint?: string }

/**
 * Resumo metodológico padronizado (camada 2). Cada campo é opcional: `undefined` = não se aplica ao módulo (não aparece);
 * `null` = deveria existir mas ainda não há o dado (aparece como estado honesto).
 */
export function CredibilitySummary({ method, sources, sourcesNote, period, updatedAt, confidence, natures, naturesTitle = 'Natureza das afirmações', gaps, gapsNote, version, children, title = 'Metodologia, evidências e confiança' }: {
  method: ReactNode; sources?: number | null; sourcesNote?: ReactNode; period?: ReactNode | null; updatedAt?: string | null; confidence?: ReactNode | null;
  natures?: NatureCount[]; naturesTitle?: string; gaps?: number | null; gapsNote?: ReactNode; version?: number | string | null; children?: ReactNode; title?: string;
}) {
  const id = useId();
  const date = (iso: string) => new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  const missing = (txt: string) => <span className="gd-missing">{txt}</span>;
  return (
    <section className="card gd-cred" aria-labelledby={`${id}-t`}>
      <h2 className="card-title" id={`${id}-t`}>{title}</h2>
      <dl className="gd-cred-grid">
        <div><dt>Metodologia</dt><dd>{method}</dd></div>
        {sources !== undefined && <div><dt>Fontes</dt><dd>{sources === null ? missing('Fonte ainda não disponível') : sources === 0 ? missing('Nenhuma fonte localizada até agora') : `${sources} fonte(s)`}{sourcesNote ? <span className="cell-sub wide">{sourcesNote}</span> : null}</dd></div>}
        {period !== undefined && <div><dt>Período analisado</dt><dd>{period ?? missing('Período não informado')}</dd></div>}
        {updatedAt !== undefined && <div><dt>Última atualização</dt><dd>{updatedAt ? date(updatedAt) : missing('Análise ainda não executada')}</dd></div>}
        {confidence !== undefined && <div><dt>Nível de confiança</dt><dd>{confidence ?? missing('Sem evidência suficiente para avaliar')}</dd></div>}
        {gaps !== undefined && <div><dt>Lacunas</dt><dd>{gaps === null ? missing('Ainda não avaliadas') : gaps === 0 ? 'Nenhuma lacuna apontada' : `${gaps} lacuna(s) de informação`}{gapsNote ? <span className="cell-sub wide">{gapsNote}</span> : null}</dd></div>}
        {version !== undefined && <div><dt>Versão da análise</dt><dd>{version === null ? missing('Sem versão registrada') : typeof version === 'number' ? `versão ${version}` : version}</dd></div>}
      </dl>
      {natures && natures.length > 0 && (
        <div className="gd-natures">
          <h3 className="gd-h3">{naturesTitle}</h3>
          <ul className="gd-nature-list">{natures.map((n) => (
            <li key={n.label} title={n.hint}><StatusPill tone={n.tone ?? 'gray'}>{n.label}</StatusPill> <b>{n.count === null ? '—' : n.count}</b>{n.count === null && <span className="sr-only"> (não classificado)</span>}</li>
          ))}</ul>
        </div>
      )}
      {children}
    </section>
  );
}

// IM-UX-005 (IM-UI-083) — três estados padronizados para a base de uma afirmação ou nota. Ausência de dado nunca vira
// zero: "sem evidência suficiente" é um estado próprio, explicado ao lado.
export type EvidenceStateKey = 'OBSERVADO' | 'INFERIDO' | 'SEM_EVIDENCIA';
export const EVIDENCE_STATE_LABEL: Record<EvidenceStateKey, string> = { OBSERVADO: 'Observado', INFERIDO: 'Inferido', SEM_EVIDENCIA: 'Sem evidência suficiente' };
export const EVIDENCE_STATE_HINT: Record<EvidenceStateKey, string> = {
  OBSERVADO: 'Sustentado diretamente por dados ou evidências.',
  INFERIDO: 'Calculado ou interpretado a partir do contexto disponível — trate como hipótese.',
  SEM_EVIDENCIA: 'Não existe base adequada para conclusão. Fica fora da conta, não vale zero.',
};
const EVIDENCE_STATE_TONE: Record<EvidenceStateKey, string> = { OBSERVADO: 'mint', INFERIDO: 'amber', SEM_EVIDENCIA: 'gray' };

/** Etiqueta do estado da evidência: rótulo + explicação curta (title e texto para leitor de tela; não depende de cor). */
export function EvidenceState({ state, detail }: { state: EvidenceStateKey; detail?: string }) {
  return (
    <span className="gd-ev-state" title={EVIDENCE_STATE_HINT[state]}>
      <StatusPill tone={EVIDENCE_STATE_TONE[state]}>{EVIDENCE_STATE_LABEL[state]}{detail ? ` · ${detail}` : ''}</StatusPill>
      <span className="sr-only"> — {EVIDENCE_STATE_HINT[state]}</span>
    </span>
  );
}

export interface ActionHelp {
  /** id estável para aria-describedby do botão. */
  id: string;
  label: string;
  what: string;
  when: string;
  newData: string;
  newVersion: string;
  preserved: string;
}

/** Explicação das ações antes de executar. Os botões apontam para cada bloco com aria-describedby={help.id}. */
export function ActionGuide({ title, actions }: { title: string; actions: ActionHelp[] }) {
  return (
    <details className="card gd-actions">
      <summary>{title}</summary>
      <div className="gd-actions-grid">
        {actions.map((a) => (
          <div key={a.id} id={a.id} className="gd-action">
            <h3 className="gd-h3">{a.label}</h3>
            <p>{a.what}</p>
            <dl className="gd-action-dl">
              <dt>Quando usar</dt><dd>{a.when}</dd>
              <dt>Pesquisa dados novos?</dt><dd>{a.newData}</dd>
              <dt>Cria versão nova?</dt><dd>{a.newVersion}</dd>
              <dt>A versão atual é preservada?</dt><dd>{a.preserved}</dd>
            </dl>
          </div>
        ))}
      </div>
    </details>
  );
}
